import React from 'react';
import styled, { css } from 'styled-components';

const ButtonStyle = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-family: 'bold', sans-serif;
  font-weight: 700;
  text-decoration: none;
  border: none !important;
  outline: none;
  cursor: pointer;
  transition: all 0.22s cubic-bezier(0.2, 0.8, 0.2, 1);
  user-select: none;
  vertical-align: middle;
  position: relative;
  overflow: hidden;

  ${({ $isIcon }) =>
    $isIcon
      ? css`
          width: 34px;
          height: 34px;
          min-width: 34px;
          padding: 0;
          border-radius: 50%;
          font-size: 13px;
          background: linear-gradient(135deg, #ff5e04 0%, #ff8038 100%);
          color: #ffffff;
          box-shadow: 0 3px 8px rgba(255, 94, 4, 0.38);

          &:hover:not(:disabled) {
            background: linear-gradient(135deg, #e65100 0%, #ff5e04 100%);
            box-shadow: 0 5px 14px rgba(255, 94, 4, 0.55);
            transform: scale(1.12);
            color: #ffffff;
          }

          &:active:not(:disabled) {
            transform: scale(0.92);
          }
        `
      : css`
          font-size: 16px;
          letter-spacing: 0.3px;
          padding: 12px 28px;
          border-radius: 50px;
          width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};

          /* Default / Primary (Festive Warm Orange Fire Theme) */
          background: linear-gradient(135deg, #ff4500 0%, #ff6a00 50%, #ff8c1a 100%);
          color: #ffffff;
          box-shadow: 0 4px 15px rgba(255, 75, 0, 0.4), 0 2px 4px rgba(0, 0, 0, 0.08);

          &:hover:not(:disabled) {
            background: linear-gradient(135deg, #e03e00 0%, #ff5500 50%, #ff7700 100%);
            box-shadow: 0 8px 24px rgba(255, 75, 0, 0.55), 0 3px 6px rgba(0, 0, 0, 0.1);
            transform: translateY(-2px) scale(1.02);
            color: #ffffff;
          }

          &:active:not(:disabled) {
            transform: translateY(1px) scale(0.98);
            box-shadow: 0 2px 6px rgba(255, 75, 0, 0.3);
          }

          /* Success Variant (Green) */
          ${({ $variant }) =>
            $variant === 'success' &&
            css`
              background: linear-gradient(135deg, #10b981 0%, #059669 100%);
              color: #ffffff;
              box-shadow: 0 4px 15px rgba(16, 185, 129, 0.4);

              &:hover:not(:disabled) {
                background: linear-gradient(135deg, #059669 0%, #047857 100%);
                box-shadow: 0 8px 24px rgba(16, 185, 129, 0.55);
                transform: translateY(-2px) scale(1.02);
                color: #ffffff;
              }
            `}

          /* Secondary / Cancel Variant (Slate Gray) */
          ${({ $variant }) =>
            ($variant === 'secondary' || $variant === 'cancel') &&
            css`
              background: linear-gradient(135deg, #64748b 0%, #475569 100%);
              color: #ffffff;
              box-shadow: 0 4px 14px rgba(100, 116, 139, 0.35);

              &:hover:not(:disabled) {
                background: linear-gradient(135deg, #475569 0%, #334155 100%);
                box-shadow: 0 7px 20px rgba(100, 116, 139, 0.45);
                transform: translateY(-2px) scale(1.02);
                color: #ffffff;
              }
            `}
        `}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    box-shadow: none;
    transform: none !important;
  }
`;

const CloseStyle = styled.button`
  font-size: 18px;
  border: 0 !important;
  color: #333;
  background: none;
  cursor: pointer;
  padding: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease, transform 0.2s ease;

  &:hover {
    color: #dc3545;
    transform: scale(1.1);
  }
`;

const Buttons = ({
  label,
  onClick,
  fullWidth,
  className = '',
  disabled,
  variant,
  type = 'button',
  ...rest
}) => {
  const isIcon = className.includes('mx-2') || className.includes('btn-icon');

  let detectedVariant = variant;
  if (!detectedVariant) {
    let text = '';
    if (typeof label === 'string') {
      text = label;
    } else if (label?.props?.children) {
      if (typeof label.props.children === 'string') {
        text = label.props.children;
      } else if (Array.isArray(label.props.children)) {
        text = label.props.children.filter((c) => typeof c === 'string').join(' ');
      }
    }
    const lower = text.toLowerCase();
    if (lower.includes('cancel')) {
      detectedVariant = 'secondary';
    } else if (lower.includes('submit')) {
      detectedVariant = 'success';
    }
  }

  return (
    <ButtonStyle
      type={type}
      onClick={onClick}
      $fullWidth={fullWidth}
      className={className}
      disabled={disabled}
      $isIcon={isIcon}
      $variant={detectedVariant}
      {...rest}
    >
      {label}
    </ButtonStyle>
  );
};

const Close = ({ label, onClick }) => {
  return <CloseStyle onClick={onClick}>{label}</CloseStyle>;
};

export { Buttons, Close }; 